'use client'
import Link from 'next/link'
import { useState } from 'react'
import ClientLogoScroller from '@/components/ClientLogoScroller'

// Demo reply drafts
const START_DRAFTS = [
  "Thank you, Priya - we’re thrilled you felt supported. We’ll pass this to the team and hope to see you again soon!",
  "We appreciate your kind words about our staff, Priya. It’s our goal to make every visit smooth and friendly!",
  "Your experience means a lot to us. Thanks for choosing us and for the five stars, Priya!"
]

const ALT_DRAFTS = [
  "Big thanks, Priya! So glad our team could help quickly. We’re here anytime you need us.",
  "You made our day, Priya - thanks for the lovely review. We can’t wait to serve you again!",
  "Appreciate the feedback! We’ll keep delivering the same care you experienced."
]

export default function Page() {
  const [drafts, setDrafts] = useState<string[]>(START_DRAFTS)
  const [message, setMessage] = useState<string | null>(null)

  const handleUseAndPost = (text: string) => {
    setMessage('Nice! This is what “Use & Post” feels like. Create an account to post for real.')
    setTimeout(() => setMessage(null), 3000)
  }

  const handleRegenerate = () => {
    setDrafts(prev => prev === START_DRAFTS ? ALT_DRAFTS : START_DRAFTS)
  }

  const features = [
    { title: 'AI drafts in your voice', desc: 'Set your tone once (friendly, professional, warm or custom).' },
    { title: 'One-click posting', desc: 'Approve and publish without leaving your dashboard.' },
    { title: 'Auto-approve 5★', desc: 'Hands-off replies for positive reviews (optional).' },
    { title: 'Language aware', desc: 'Reply in the same language as your customer.' },
    { title: 'Daily/weekly digests', desc: 'Stay on top of new reviews with summaries.' },
    { title: 'Multi-location ready', desc: 'Manage multiple listings from one place.' },
  ]

  // Pricing helpers
  function formatPrice(currency: 'USD' | 'INR', usd: number, inr: number) {
    return currency === 'USD'
      ? `$${usd}`
      : new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(inr)
  }

  const [currency, setCurrency] = useState<'USD' | 'INR'>('INR')

  const plans = [
    {
      name: 'Free',
      usd: 0, inr: 0,
      limit: '10 replies / month',
      bullets: ['1 location', 'Manual approval', 'Email support (48h)'],
      cta: 'Start free',
    },
    {
      name: 'Starter',
      usd: 6, inr: 499,
      limit: '20 replies / month',
      bullets: ['1–2 locations', 'Auto-approve 5★', 'Daily review email', 'Saved tone presets'],
      cta: 'Choose Starter',
      highlight: true,
    },
    {
      name: 'Pro',
      usd: 23, inr: 1999,
      limit: '50 replies / month',
      bullets: ['Up to 5 locations', 'Weekly PDF insights', 'Custom brand tone', 'Priority support (24h)'],
      cta: 'Choose Pro',
    },
  ]

  const testimonials = [
    { quote: 'PingReply cut our reply time to minutes - and customers noticed.', author: 'Anita, Clinic Owner' },
    { quote: 'We reply to every review now. Simple, fast, on-brand.', author: 'Rahul, Restaurant Manager' },
    { quote: 'Perfect for our multi-location workflow. One click and done.', author: 'Maya, Agency Lead' },
  ]

  const faqs = [
    { q: 'Is this safe with Google?', a: 'Yes - we use the official Business Profile APIs with your permission.' },
    { q: 'Can I edit replies?', a: 'Always. Edit/review any draft before posting.' },
    { q: 'Multiple locations?', a: 'Supported on Starter and above.' },
    { q: 'Will this help SEO?', a: 'Consistent owner replies improve trust and visibility.' },
  ]

  return (
    <>
      {/* ============================= */}
      {/* HERO — NEW with beta badge   */}
      {/* ============================= */}
      <section className="container pt-14 pb-10">
        <div className="grid items-center gap-10 md:grid-cols-2">

          {/* Left */}
          <div className="space-y-6">
            {/* Brand + beta */}
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="text-2xl font-semibold tracking-tight">
                <span className="text-gray-900 dark:text-white">ping</span>
                <span className="text-blue-600">replies</span>
              </span>
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                beta
              </span>
            </div>

            <h1 className="h1">Reply to Google reviews in seconds - perfectly on-brand.</h1>
            <p className="lead">
              PingReplies writes and posts thoughtful owner replies for every review.
              Save hours weekly, boost trust, and lift local SEO.
            </p>

            <div className="flex gap-3">
              <Link href="/signup" className="btn-primary">Get started free</Link>
              <Link href="/login" className="btn-ghost">I already have an account</Link>
            </div>

            <div className="flex items-center gap-4 text-xs text-muted">
              <span>★ Improves response rate</span>
              <span>One-click posting • No code</span>
            </div>

            {message && <p className="text-sm text-emerald-600">{message}</p>}
          </div>

          {/* Right — preview card */}
          <div className="card p-5 dark:bg-[#0e1524] dark:border-white/10">
            <p className="text-sm text-muted mb-2">Preview</p>
            <div className="rounded-xl border p-4 dark:border-white/10">
              <div className="flex items-center justify-between mb-1">
                <p className="font-medium">Priya • ★★★★★</p>
                <span className="text-xs text-muted">2h ago</span>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-200">“Great staff and quick support!”</p>

              <div className="mt-4 grid gap-2">
                {drafts.map((d, i) => (
                  <div key={i} className="rounded-xl border p-3 text-sm dark:border-white/10">
                    {d}
                  </div>
                ))}
              </div>

              <div className="mt-4 flex gap-2">
                <button className="btn-primary" onClick={() => handleUseAndPost(drafts[0])}>
                  Use &amp; Post
                </button>
                <button className="btn-ghost" onClick={handleRegenerate}>Regenerate</button>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ============================= */}
      {/* CLIENT LOGOS — NEW headline  */}
      {/* ============================= */}
      <section className="container py-12">
        

        <ClientLogoScroller />
      </section>


      {/* ============================= */}
      {/* FEATURES                      */}
      {/* ============================= */}
      <section id="features" className="container py-16">
           <h2 className="h2 text-center">Everything you need to reply well - fast</h2>
          <p className="lead mt-2 text-center">From fetching reviews to posting replies - one clean workflow.</p>
 
        <div className="grid gap-6 mt-10 md:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="card p-5 dark:bg-[#0e1524] dark:border-white/10">
              <p className="font-medium">{f.title}</p>
              <p className="text-sm text-muted mt-1">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>


      {/* ============================= */}
      {/* PRICING                       */}
      {/* ============================= */}
      <section id="pricing" className="container py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="h2">Simple, fair pricing</h2>
          <p className="lead">Start free. Upgrade when you're ready.</p>

          <div className="inline-flex mt-4 rounded-xl border overflow-hidden dark:border-white/10">
            <button
              className={`px-4 py-2 text-sm ${currency === 'USD' ? 'bg-brand-500 text-white' : 'hover:bg-black/5 dark:hover:bg-white/10'}`}
              onClick={() => setCurrency('USD')}
            >
              USD $
            </button>
            <button
              className={`px-4 py-2 text-sm ${currency === 'INR' ? 'bg-brand-500 text-white' : 'hover:bg-black/5 dark:hover:bg-white/10'}`}
              onClick={() => setCurrency('INR')}
            >
              INR ₹
            </button>
          </div>
        </div>

        <div className="grid gap-6 mt-10 md:grid-cols-3">
          {plans.map((p) => (
            <div key={p.name} className={`card p-6 dark:bg-[#0e1524] dark:border-white/10 ${p.highlight ? 'ring-2 ring-brand-500' : ''}`}>
              <div className="flex items-baseline justify-between">
                <p className="font-semibold">{p.name}</p>
                <p className="text-2xl font-semibold">
                  {formatPrice(currency, p.usd, p.inr)}
                  <span className="text-sm text-muted">/mo</span>
                </p>
              </div>

              <p className="mt-2 text-sm font-medium">{p.limit}</p>

              <ul className="mt-4 space-y-2 text-sm text-muted">
                {p.bullets.map((b) => <li key={b}>• {b}</li>)}
              </ul>

              <a href="/signup" className="btn-primary mt-6">{p.cta}</a>
            </div>
          ))}
        </div>

        <p className="text-xs text-muted text-center mt-4">
          No card required for Free. Upgrade anytime.
        </p>
      </section>


      {/* ============================= */}
      {/* TESTIMONIALS                  */}
      {/* ============================= */}
      <section id="testimonials" className="container py-16">
           <h2 className="h2 text-center">Loved by busy teams</h2>
          <p className="lead mt-2 text-center">Real outcomes from real businesses.</p>
 
        <div className="grid gap-6 mt-8 md:grid-cols-3">
          {testimonials.map((t) => (
            <figure key={t.author} className="card p-5 dark:bg-[#0e1524] dark:border-white/10">
              <blockquote className="text-sm text-ink dark:text-gray-100">“{t.quote}”</blockquote>
              <figcaption className="mt-3 text-xs text-muted">{t.author}</figcaption>
            </figure>
          ))}
        </div>
      </section>


      {/* ============================= */}
      {/* FAQ                           */}
      {/* ============================= */}
      <section id="faq" className="container py-16">
           <h2 className="h2 text-center">FAQ</h2>
          <p className="lead mt-2 text-center">Short answers to common questions.</p>
 
        <div className="grid gap-4 mt-8 md:grid-cols-2">
          {faqs.map((f) => (
            <div key={f.q} className="card p-5 dark:bg-[#0e1524] dark:border-white/10">
              <p className="font-medium">{f.q}</p>
              <p className="text-sm text-muted mt-1">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  )
}
